(() => {
  const VERSION = '2026-08-12.2';
  if (window.PBLogoSavedPhase1?.version === VERSION) return;

  const ROOT_SELECTOR = '[id^="PBPhotoBoxCustomizer-"], [id^="PBUSBBoxCustomizer-"], [id^="PBStandaloneUSBCustomizer-"]';
  const MODAL_SELECTOR = '.pb-photo-customizer__modal, .pb-usb-customizer__modal, .pb-standalone-usb-customizer__modal, [data-pb-customizer-modal]';
  const PANEL_SELECTOR = '[data-pb-logo-v3="box"], [data-pb-logo-v3="addon"]';
  const HELP_HTML = '<strong>Preview tip:</strong> SVG works best. Upload dark artwork on a white or transparent background to preview it in your selected foil color. PDF and transparent PNG are also accepted.';

  const artworkCache = new Map();
  const renderTokens = new WeakMap();
  const contexts = [];
  let scheduled = false;

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => root ? [...root.querySelectorAll(selector)] : [];

  const injectStyles = () => {
    let style = document.getElementById('pb-logo-saved-phase1-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'pb-logo-saved-phase1-style';
      document.head.appendChild(style);
    }

    style.textContent = `
      .pb-logo-v3__source.pb-logo-phase1__visual-source{
        display:grid!important;
        grid-template-columns:64px minmax(0,1fr) auto!important;
        align-items:center!important;
        gap:.85rem!important;
        min-height:0!important;
        padding:.7rem .85rem!important;
      }
      .pb-logo-v3__source.pb-logo-phase1__visual-source > span:not(.pb-logo-phase1__thumb):not(.pb-logo-v3__source-price){
        min-width:0;
      }
      .pb-logo-phase1__thumb{
        display:flex;
        align-items:center;
        justify-content:center;
        width:64px;
        height:52px;
        padding:.35rem;
        overflow:hidden;
        border:1px solid rgba(var(--color-foreground),.12);
        border-radius:5px;
        background:#fff;
      }
      .pb-logo-phase1__thumb img{
        display:block;
        max-width:100%;
        max-height:100%;
        width:auto;
        height:auto;
        object-fit:contain;
      }
      .pb-logo-phase1__visual-source .pb-logo-v3__source-price{
        align-self:center;
        font-size:1.1rem;
      }
      @media(max-width:549px){
        .pb-logo-v3__source.pb-logo-phase1__visual-source{
          grid-template-columns:54px minmax(0,1fr)!important;
        }
        .pb-logo-phase1__thumb{width:54px;height:46px;}
        .pb-logo-phase1__visual-source .pb-logo-v3__source-price{
          grid-column:2;
          justify-self:start;
        }
      }
    `;
  };

  const ensureSetupDisclaimer = scope => {
    qa(scope, '[data-pb-logo="box"], [data-pb-logo="addon"]').forEach(button => {
      const wrap = q(button, ':scope > .pb-v22-price-wrap');
      if (!wrap) return;
      let note = q(wrap, ':scope > small');
      if (!note) {
        note = document.createElement('small');
        wrap.appendChild(note);
      }
      note.textContent = 'New logo die: +$30 one-time setup';
    });
  };

  const simplifyHelp = scope => {
    qa(scope, '.pb-logo-v3__preview-help').forEach(el => {
      if (el.innerHTML !== HELP_HTML) el.innerHTML = HELP_HTML;
    });
    qa(scope, '[data-pb-logo-v3-file-status]').forEach(el => {
      if (/SVG preferred|PDF|PNG/i.test(el.textContent || '')) {
        el.textContent = 'SVG preferred. PDF and transparent PNG accepted.';
      }
    });
  };

  const normalizeCandidate = candidate => {
    if (!candidate || typeof candidate !== 'object') return candidate;

    if (candidate.source === 'saved-new-profile') {
      candidate.source = 'saved';
      candidate.dieId = candidate.dieId || candidate.sourceDieId;
      candidate.requiresSetup = false;
      candidate.needsUpload = false;
      candidate.detail = 'Saved logo — ready to reuse';
    } else if (candidate.source === 'saved') {
      candidate.requiresSetup = false;
      candidate.needsUpload = false;
      candidate.detail = 'Saved logo — ready to reuse';
    } else if (candidate.source === 'cart-new-profile') {
      candidate.source = 'cart';
      candidate.requestId = candidate.requestId || candidate.sourceRequestId;
      candidate.requiresSetup = true;
      candidate.detail = 'Already uploaded for this order — setup applies once';
    } else if (candidate.source === 'cart') {
      candidate.detail = candidate.requiresSetup
        ? 'Already uploaded for this order — setup applies once'
        : 'Already uploaded for this order — ready to reuse';
    }

    return candidate;
  };

  const artworkUrl = candidate => String(candidate?.previewUrl || candidate?.artworkUrl || '');

  const setPriceAndDetail = button => {
    const candidate = normalizeCandidate(button?._pbCandidate);
    if (!candidate) return;

    const copy = [...button.children].find(el =>
      el.tagName === 'SPAN' &&
      !el.classList.contains('pb-logo-phase1__thumb') &&
      !el.classList.contains('pb-logo-v3__source-price')
    );
    const detail = copy?.querySelector?.('small');
    const price = q(button, '.pb-logo-v3__source-price');

    if (detail && candidate.detail) detail.textContent = candidate.detail;
    if (price) {
      if (candidate.source === 'saved') price.textContent = 'No setup fee';
      else if (candidate.source === 'cart') price.textContent = candidate.requiresSetup ? '+$30 setup' : 'No setup fee';
      else if (candidate.source === 'new') price.textContent = '+$30 one-time setup';
    }
  };

  const ensureThumbnail = button => {
    if (!button || button.querySelector(':scope > .pb-logo-phase1__thumb')) return;
    const candidate = normalizeCandidate(button._pbCandidate);
    const url = artworkUrl(candidate);
    if (!candidate || !url || !['saved', 'cart'].includes(candidate.source)) return;

    const thumb = document.createElement('span');
    thumb.className = 'pb-logo-phase1__thumb';
    const img = document.createElement('img');
    img.src = url;
    img.alt = candidate.name ? `${candidate.name} logo` : 'Saved logo';
    img.loading = 'lazy';
    thumb.appendChild(img);
    button.insertBefore(thumb, button.firstChild);
    button.classList.add('pb-logo-phase1__visual-source');
  };

  const promoteMismatchSources = panel => {
    const sections = qa(panel, ':scope > .pb-logo-v3__section-title');
    const mismatchTitle = sections.find(el => /new die size|existing artwork/i.test(el.textContent || ''));
    if (!mismatchTitle) return;

    const extraSources = mismatchTitle.nextElementSibling;
    const firstSources = q(panel, ':scope > .pb-logo-v3__sources');
    if (firstSources && extraSources?.classList?.contains('pb-logo-v3__sources')) {
      [...extraSources.children].forEach(child => firstSources.appendChild(child));
      extraSources.remove();
    }
    mismatchTitle.remove();
  };

  const improvePanelCopy = panel => {
    const intro = q(panel, ':scope > p');
    if (intro) intro.textContent = 'Choose a saved logo or upload a new logo.';
  };

  const hexToRgb = hex => {
    const clean = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(clean)) return null;
    const full = clean.length === 3 ? clean.split('').map(x => x + x).join('') : clean;
    const number = Number.parseInt(full, 16);
    return { r: (number >> 16) & 255, g: (number >> 8) & 255, b: number & 255 };
  };

  const decodeArtwork = async url => {
    if (!url) throw new Error('No artwork URL');
    if (artworkCache.has(url)) return artworkCache.get(url);

    const promise = (async () => {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`Artwork request failed (${response.status})`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const image = new Image();
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = objectUrl;
        });
        const naturalW = image.naturalWidth || image.width || 800;
        const naturalH = image.naturalHeight || image.height || 400;
        const scale = Math.min(1, 1200 / Math.max(naturalW, naturalH));
        const width = Math.max(1, Math.round(naturalW * scale));
        const height = Math.max(1, Math.round(naturalH * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(image, 0, 0, width, height);
        const source = ctx.getImageData(0, 0, width, height);
        return { width, height, pixels: new Uint8ClampedArray(source.data) };
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    })();

    artworkCache.set(url, promise);
    try {
      return await promise;
    } catch (error) {
      artworkCache.delete(url);
      throw error;
    }
  };

  const foilColorFor = (scope, kind) => {
    if (kind === 'addon') {
      return q(scope, '[data-box-foil-name].is-active')?.dataset?.boxFoilColor || '#d9bd6a';
    }
    return q(scope, '[data-foil].is-active')?.dataset?.foilColor || '#C8A95A';
  };

  const renderSavedPreview = async (ctx, panel, candidate) => {
    candidate = normalizeCandidate(candidate);
    if (!ctx || !panel || candidate?.source !== 'saved') return;

    const kind = panel.dataset.pbLogoV3 || 'box';
    const preview = q(ctx.scope, `[data-pb-logo-v3-preview="${kind}"]`);
    const url = artworkUrl(candidate);
    if (!preview || !url) return;

    const color = foilColorFor(ctx.scope, kind);
    const key = `${url}|${color}`;
    const existing = q(preview, 'img[data-pb-logo-saved-phase1]');
    if (existing?.dataset?.pbLogoSavedPhase1 === key) return;

    const token = Symbol(key);
    renderTokens.set(preview, token);

    try {
      const source = await decodeArtwork(url);
      if (renderTokens.get(preview) !== token) return;
      const rgb = hexToRgb(color);
      if (!rgb) return;

      const pixels = new Uint8ClampedArray(source.pixels);
      for (let i = 0; i < pixels.length; i += 4) {
        const alpha = pixels[i + 3] / 255;
        if (!alpha) continue;
        const lum = (0.2126 * pixels[i]) + (0.7152 * pixels[i + 1]) + (0.0722 * pixels[i + 2]);
        const darkness = Math.max(0, Math.min(1, (245 - lum) / 220));
        pixels[i] = rgb.r;
        pixels[i + 1] = rgb.g;
        pixels[i + 2] = rgb.b;
        pixels[i + 3] = Math.round(255 * alpha * darkness);
      }

      const canvas = document.createElement('canvas');
      canvas.width = source.width;
      canvas.height = source.height;
      const canvasCtx = canvas.getContext('2d');
      const imageData = canvasCtx.createImageData(source.width, source.height);
      imageData.data.set(pixels);
      canvasCtx.putImageData(imageData, 0, 0);

      if (renderTokens.get(preview) !== token) return;
      preview.innerHTML = '';
      const img = document.createElement('img');
      img.alt = candidate.name ? `${candidate.name} foil preview` : 'Saved logo foil preview';
      img.src = canvas.toDataURL('image/png');
      img.dataset.pbLogoSavedPhase1 = key;
      preview.appendChild(img);
      preview.hidden = false;
    } catch (error) {
      console.warn('PB saved logo foil preview unavailable', error);
    }
  };

  const enhancePanel = (ctx, panel) => {
    if (!panel) return;
    promoteMismatchSources(panel);
    improvePanelCopy(panel);

    qa(panel, '.pb-logo-v3__source').forEach(button => {
      normalizeCandidate(button._pbCandidate);
      setPriceAndDetail(button);
      ensureThumbnail(button);
    });

    const active = q(panel, '.pb-logo-v3__source.is-active');
    const candidate = normalizeCandidate(active?._pbCandidate);
    if (candidate?.source === 'saved') renderSavedPreview(ctx, panel, candidate);
  };

  const enhanceContext = ctx => {
    ensureSetupDisclaimer(ctx.scope);
    simplifyHelp(ctx.scope);
    qa(ctx.scope, PANEL_SELECTOR).forEach(panel => enhancePanel(ctx, panel));
  };

  const run = () => {
    scheduled = false;
    contexts.forEach(enhanceContext);
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(run);
  };

  injectStyles();
  qa(document, ROOT_SELECTOR).forEach(root => {
    const scope = q(root, MODAL_SELECTOR) || root;
    contexts.push({ root, scope });
  });
  run();

  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'hidden', 'src']
  });

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const source = target.closest('.pb-logo-v3__source');
    if (source?._pbCandidate) normalizeCandidate(source._pbCandidate);

    if (target.closest('.pb-logo-v3__source, [data-foil], [data-box-foil-name], [data-pb-logo]')) {
      setTimeout(schedule, 0);
      setTimeout(schedule, 40);
      setTimeout(schedule, 120);
    }
  }, true);

  document.addEventListener('change', event => {
    if (contexts.some(ctx => ctx.scope.contains(event.target) || ctx.root.contains(event.target))) {
      setTimeout(schedule, 0);
      setTimeout(schedule, 60);
    }
  });

  window.PBLogoSavedPhase1 = {
    version: VERSION,
    refresh: run
  };
})();
